<script lang="ts">
export const elInputCheckboxSizes = ['xs', 'xxs'] as const;
export type ElInputCheckboxSizes = (typeof elInputCheckboxSizes)[number];

export const elInputCheckboxColors = [
  'primary',
  'secondary',
  'brand-blue',
  'brand-green',
  'green',
  'purple',
  'indigo',
  'yellow',
  'orange',
  'cyan',
  'red',
  'pink',
  'rose',
  'gray',
] as const;
export type ElInputCheckboxColors = (typeof elInputCheckboxColors)[number];

export const elInputCheckboxModelValues = [true, false, 'indeterminate'] as const;
export type ElInputCheckboxModelValues = (typeof elInputCheckboxModelValues)[number];
</script>

<script lang="ts" setup>
import { computed, PropType, useSlots } from 'vue';
import ElInputContainer from '@/forms/ElInputContainer.vue';
import { useInput, withNumberElInputProps } from '@/forms/input';

const props = defineProps({
  ...withNumberElInputProps(),
  modelValue: {
    type: [Boolean, String] as PropType<ElInputCheckboxModelValues>,
    default: false,
    required: true,
  },
  size: {
    type: String as PropType<ElInputCheckboxSizes>,
    default: 'xs',
    required: false,
  },
  color: {
    type: String as PropType<ElInputCheckboxColors>,
    default: 'primary',
    required: false,
  },
  inputClass: {
    type: String,
    default: 'text-neutral-darker',
  },
});

const emit = defineEmits<{
  (event: 'update:modelValue', val: ElInputCheckboxModelValues): void;
}>();

const computedModelValue = computed(() => props.modelValue || false);
const computedValidation = computed(() => [props.validation]);

const slots = useSlots();

const { value, errorMessage, uuid } = useInput(
  computedModelValue,
  computedValidation,
  props.name,
  (newValue: ElInputCheckboxModelValues) => {
    emit('update:modelValue', newValue);
  },
);

const toggleCheckbox = () => {
  if (props.disabled) return;
  if (props.modelValue === 'indeterminate') {
    value.value = true;
  } else {
    value.value = !value.value;
  }
};
</script>

<template>
  <ElInputContainer v-bind="$attrs" :hidden-error-message="true" :no-icon="true" :name="uuid">
    <input
      :id="uuid"
      :name="props.name"
      :checked="typeof modelValue === 'boolean' && modelValue"
      :indeterminate="modelValue === 'indeterminate'"
      type="checkbox"
      class="sr-only"
      :disabled="disabled"
      :data-cy="label ? `${label.trim().replaceAll(' ', '')}-checkbox` : 'checkbox'"
      hidden
    />

    <div class="flex gap-2 items-start">
      <label
        role="checkbox"
        class="flex items-start justify-center gap-2"
        :class="{ 'cursor-not-allowed': disabled, 'cursor-pointer': !disabled }"
        @click="toggleCheckbox"
      >
        <!-- Checkbox container -->
        <div
          class="flex justify-center items-center"
          :class="{
            'h-6 w-6': size === 'xs',
            'h-5 w-5': size === 'xxs',
          }"
        >
          <!-- Checkbox border/background -->
          <div
            class="rounded focus:ring-0 flex items-center justify-center shrink-0"
            :class="{
              'h-5 w-5': size === 'xs',
              'h-4 w-4': size === 'xxs',
              'border-2': !value,
              'border-neutral-inactive': !value && disabled,
              'border-neutral-500': (color === 'primary' || color === 'secondary') && !value && !disabled,
              'border-brandBlue-500': color === 'brand-blue' && !value && !disabled,
              'border-brandGreen-500': color === 'brand-green' && !value && !disabled,
              'border-green-500': color === 'green' && !value && !disabled,
              'border-purple-500': color === 'purple' && !value && !disabled,
              'border-indigo-500': color === 'indigo' && !value && !disabled,
              'border-yellow-500': color === 'yellow' && !value && !disabled,
              'border-orange-500': color === 'orange' && !value && !disabled,
              'border-cyan-500': color === 'cyan' && !value && !disabled,
              'border-pink-500': color === 'pink' && !value && !disabled,
              'border-rose-500': color === 'rose' && !value && !disabled,
              'border-gray-500': color === 'gray' && !value && !disabled,
              'border-red-500':
                (color === 'red' && !value && !disabled) ||
                ((props.errorMessage === false ? false : props.errorMessage || errorMessage) && !hiddenErrorMessage),
              'bg-neutral-inactive': value && disabled,
              'bg-primary-active': color === 'primary' && value && !disabled,
              'bg-secondary-active': color === 'secondary' && value && !disabled,
              'bg-brandBlue-500': color === 'brand-blue' && value && !disabled,
              'bg-brandGreen-500': color === 'brand-green' && value && !disabled,
              'bg-green-500': color === 'green' && value && !disabled,
              'bg-purple-500': color === 'purple' && value && !disabled,
              'bg-indigo-500': color === 'indigo' && value && !disabled,
              'bg-yellow-500': color === 'yellow' && value && !disabled,
              'bg-orange-500': color === 'orange' && value && !disabled,
              'bg-cyan-500': color === 'cyan' && value && !disabled,
              'bg-red-500': color === 'red' && value && !disabled,
              'bg-pink-500': color === 'pink' && value && !disabled,
              'bg-rose-500': color === 'rose' && value && !disabled,
              'bg-gray-500': color === 'gray' && value && !disabled,
              inputClass,
            }"
          >
            <!-- Minus icon -->
            <div
              v-if="modelValue === 'indeterminate'"
              class="bg-white"
              :class="{
                'h-[0.2rem] w-3': size === 'xs',
                'h-[0.15rem] w-[0.6rem]': size === 'xxs',
              }"
            ></div>

            <!-- Check icon -->
            <div
              v-else-if="value"
              class="border border-neutral-inverse rotate-45 border-t-0 border-l-0"
              :class="{
                'w-[0.5rem] h-[0.8rem] border-b-[0.2rem] border-r-[0.2rem] -mt-[0.15rem]': size === 'xs',
                'w-[0.35rem] h-[0.7rem] border-b-[0.15rem] border-r-[0.15rem] -mt-[0.15rem]': size === 'xxs',
              }"
            ></div>
          </div>
        </div>

        <div
          v-if="label"
          :for="uuid"
          class="font-normal text-left"
          :class="{
            'text-base mt-[0.1rem]': size === 'xs',
            'text-sm mt-[0.05rem]': size === 'xxs',
            'text-neutral-darker cursor-pointer': !disabled,
            'text-neutral-inactive cursor-not-allowed': disabled,
          }"
        >
          {{ label }}
        </div>
      </label>

      <div
        v-if="slots.default"
        class="font-normal text-left"
        :class="{
          'text-base mt-[0.1rem]': size === 'xs',
          'text-sm mt-[0.05rem]': size === 'xxs',
          'text-neutral-darker': value && !disabled,
          'text-neutral-inactive': disabled,
        }"
      >
        <slot />
      </div>
    </div>
    <template #after>
      <p
        v-if="!!(props.errorMessage === false ? false : props.errorMessage || errorMessage) && !hiddenErrorMessage"
        class="text-error mt-1 text-sm ml-8"
      >
        {{ props.errorMessage || errorMessage }}
      </p>
    </template>
  </ElInputContainer>
</template>
